import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Database, CheckCircle, XCircle, Clock, AlertCircle,
  ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, MessageSquare,
  Syringe, Shield, Info, Eye, Award
} from "lucide-react";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

export default function VetKBValidation({ vetToken }) {
  const [vaccines, setVaccines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVaccine, setSelectedVaccine] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [votingId, setVotingId] = useState(null);
  const [isVetVerified, setIsVetVerified] = useState(false);

  useEffect(() => {
    if (vetToken) {
      fetchPendingVaccines();
    }
  }, [vetToken]);

  const fetchPendingVaccines = async () => {
    setLoading(true);
    try {
      // Use the new unified endpoint that supports vet tokens
      const res = await fetch(`${API}/api/kb/vaccines-to-validate?limit=20`, {
        headers: { 'Authorization': `Bearer ${vetToken}` },
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setVaccines(data.vaccines || []);
        setIsVetVerified(data.vet_verified || false);
      } else if (res.status === 401) {
        // Token expired or invalid - don't show error, just hide component
        setVaccines([]);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (kbId, voteType) => {
    setVotingId(kbId);
    try {
      const res = await fetch(`${API}/api/kb/vaccines/${kbId}/vote`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${vetToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          vote: voteType,
          // is_vet is now determined server-side based on auth token
          suggestion: voteType === 'suggest_edit' ? suggestion : null
        })
      });
      
      if (res.ok) {
        toast.success(
          voteType === 'confirm' ? "Vaccino confermato!" :
          voteType === 'reject' ? "Vaccino segnalato" :
          "Suggerimento inviato"
        );
        // Remove from list or refresh
        setVaccines(prev => prev.filter(v => v.kb_id !== kbId));
        setShowSuggest(false);
        setSuggestion("");
      } else {
        toast.error("Errore nel salvataggio del voto");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setVotingId(null);
    }
  };

  const getProtectionLabel = (protection) => {
    const labels = {
      rabies: "Rabbia",
      distemper: "Cimurro",
      parvovirus: "Parvovirosi",
      hepatitis: "Epatite",
      leptospirosis: "Leptospirosi",
      parainfluenza: "Parainfluenza",
      bordetella: "Bordetella",
      leishmaniasis: "Leishmaniosi",
      panleukopenia: "Panleucopenia",
      calicivirus: "Calicivirus",
      herpesvirus: "Herpesvirus",
      chlamydia: "Chlamydia",
      feline_leukemia: "FeLV",
      myxomatosis: "Mixomatosi",
      rhd: "RHD"
    };
    return labels[protection] || protection;
  };

  const getSpeciesLabel = (species) => {
    const labels = {
      canine: "Cane",
      feline: "Gatto",
      equine: "Cavallo",
      rabbit: "Coniglio"
    };
    return labels[species] || species;
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-4">
          <Database className="text-blue-500" size={24} />
          <h3 className="font-bold text-lg">Validazione Vaccini KB</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center">
            <Database className="text-blue-600" size={20} />
          </div>
          <div>
            <h3 className="font-bold text-lg text-gray-900 dark:text-white">Validazione Vaccini</h3>
            <p className="text-sm text-gray-500">Aiuta a validare la Knowledge Base vaccini</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isVetVerified && (
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
              <Award size={12} /> Voto 3x
            </span>
          )}
          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
            {vaccines.length} in attesa
          </span>
        </div>
      </div>

      {vaccines.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle className="mx-auto text-green-500 mb-3" size={40} />
          <p className="text-gray-500">Tutti i vaccini sono stati validati!</p>
          <p className="text-sm text-gray-400 mt-1">Grazie per il tuo contributo</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
          {vaccines.slice(0, 10).map(vaccine => (
            <div 
              key={vaccine.kb_id}
              className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {vaccine.commercial_name}
                    </h4>
                    {vaccine.ema_code && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                        EMA
                      </span>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-0.5">
                    {vaccine.manufacturer || "Produttore non specificato"}
                  </p>
                  
                  {/* Species */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {vaccine.target_species?.map(s => (
                      <span 
                        key={s} 
                        className="text-xs px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded"
                      >
                        {getSpeciesLabel(s)}
                      </span>
                    ))}
                  </div>
                  
                  {/* Protections */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {vaccine.protection_types?.slice(0, 4).map(p => (
                      <span 
                        key={p} 
                        className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded"
                      >
                        {getProtectionLabel(p)}
                      </span>
                    ))}
                    {vaccine.protection_types?.length > 4 && (
                      <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">
                        +{vaccine.protection_types.length - 4}
                      </span>
                    )}
                  </div>
                  
                  {/* Duration */}
                  {vaccine.duration_months && (
                    <p className="text-xs text-gray-400 mt-2">
                      Durata: {vaccine.duration_months} mesi
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleVote(vaccine.kb_id, 'confirm')}
                    disabled={votingId === vaccine.kb_id}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-lg h-9 px-3"
                  >
                    <ThumbsUp size={14} className="mr-1" />
                    Conferma
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setSelectedVaccine(vaccine); setShowSuggest(true); }}
                    disabled={votingId === vaccine.kb_id}
                    className="rounded-lg h-9 px-3 border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                  >
                    <MessageSquare size={14} className="mr-1" />
                    Modifica
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVote(vaccine.kb_id, 'reject')}
                    disabled={votingId === vaccine.kb_id}
                    className="rounded-lg h-9 px-3 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <ThumbsDown size={14} className="mr-1" />
                    Rifiuta
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {vaccines.length > 10 && (
        <p className="text-center text-sm text-gray-500 mt-4">
          Mostrando 10 di {vaccines.length} vaccini in attesa
        </p>
      )}

      {/* Suggest Edit Dialog */}
      <Dialog open={showSuggest} onOpenChange={setShowSuggest}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="text-yellow-500" size={20} />
              Suggerisci Modifica
            </DialogTitle>
          </DialogHeader>
          
          {selectedVaccine && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="font-medium">{selectedVaccine.commercial_name}</p>
                <p className="text-sm text-gray-500">{selectedVaccine.manufacturer}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium block mb-2">
                  Descrivi la modifica suggerita
                </label>
                <Textarea
                  value={suggestion}
                  onChange={(e) => setSuggestion(e.target.value)}
                  placeholder="Es. La durata corretta è 12 mesi, non 36..."
                  rows={4}
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowSuggest(false)} className="flex-1">
                  Annulla
                </Button>
                <Button 
                  onClick={() => handleVote(selectedVaccine.kb_id, 'suggest_edit')}
                  disabled={!suggestion.trim() || votingId === selectedVaccine?.kb_id}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600"
                >
                  Invia Suggerimento
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
